
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import ProductDetails from "@/components/ProductDetails";
import type { Product, SizeGuide } from "@/types/product";
import { Helmet } from "react-helmet-async";

const Product = () => {
  const { id, slug } = useParams();
  const queryParam = slug || id;
  const isSlug = !!slug;
  
  console.log("Product: Rendering product page for:", isSlug ? `slug: ${slug}` : `id: ${id}`);

  const { data: product, isLoading } = useQuery({
    queryKey: ['product', queryParam],
    queryFn: async () => {
      console.log("Product: Fetching product data for:", isSlug ? `slug: ${slug}` : `id: ${id}`);
      
      // Create a query based on whether we have a slug or ID
      let query = supabase
        .from('products')
        .select(`
          id,
          name,
          description,
          price,
          published,
          has_sizes,
          has_colors,
          has_size_guide,
          size_guide,
          slug,
          product_categories!inner (
            categories!inner (
              name
            )
          ),
          product_variants (
            size,
            color_name,
            color_hex,
            position
          ),
          product_images (
            url,
            main
          )
        `);
      
      if (isSlug) {
        query = query.eq('slug', slug);
      } else {
        query = query.eq('id', id);
      }
      
      const { data, error } = await query.single();

      if (error) {
        console.error("Product: Error fetching product:", error);
        throw error;
      }

      console.log("Product: Raw data:", data);

      // Trier les variants par position
      const sortedVariants = [...data.product_variants].sort((a, b) => 
        (a.position || 0) - (b.position || 0)
      );

      return {
        id: data.id,
        name: data.name,
        description: data.description,
        price: data.price,
        categories: data.product_categories.map((pc: any) => pc.categories.name),
        published: data.published,
        has_sizes: data.has_sizes,
        has_colors: data.has_colors,
        has_size_guide: data.has_size_guide,
        size_guide: data.size_guide as SizeGuide,
        slug: data.slug,
        variants: sortedVariants.map((variant: any) => ({
          size: variant.size,
          colorName: variant.color_name,
          colorHex: variant.color_hex,
          position: variant.position
        })),
        images: data.product_images.map((image: any) => image.url)
      } as Product;
    },
    enabled: !!queryParam
  });

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="animate-pulse">
          <div className="bg-muted h-72 sm:h-96 rounded-lg mb-8"></div>
          <div className="space-y-4">
            <div className="h-4 bg-muted rounded w-3/4"></div>
            <div className="h-4 bg-muted rounded w-1/2"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">Produit non trouvé</h1>
          <p className="text-gray-600">
            Le produit que vous recherchez n'existe pas ou a été supprimé.
          </p>
        </div>
      </div>
    );
  }

  // Find main image for meta tags
  const mainImage = product.images[0] || '';
  const productUrl = window.location.href;
  const description = product.description.replace(/<[^>]*>?/gm, '').substring(0, 160);

  return (
    <>
      <Helmet>
        <title>{product.name} | Agroref</title>
        <meta name="description" content={description} />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="product" />
        <meta property="og:url" content={productUrl} />
        <meta property="og:title" content={`${product.name} | Agroref`} />
        <meta property="og:description" content={description} />
        {mainImage && <meta property="og:image" content={mainImage} />}
        
        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content={productUrl} />
        <meta property="twitter:title" content={`${product.name} | Agroref`} />
        <meta property="twitter:description" content={description} />
        {mainImage && <meta property="twitter:image" content={mainImage} />}
        
        {/* Product specific structured data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org/",
            "@type": "Product",
            "name": product.name,
            "description": description,
            "image": product.images,
            "offers": {
              "@type": "Offer",
              "priceCurrency": "CAD",
              "price": product.price,
              "availability": "https://schema.org/InStock"
            }
          })}
        </script>
      </Helmet>
      <ProductDetails product={product} />
    </>
  );
};

export default Product;
